import { ClickableTextPanel } from "@/Common/Components/ClickableText/ClickableTextPanel";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { RadioButton } from "@/Common/Components/Inputs/RadioButton";
import { SimpleTextareaResizable } from "@/Common/Components/Inputs/SimpleTextareaResizable";
import { SimpleTextareaResizableWithAutocomplete } from "@/Common/Components/Inputs/SimpleTextareaResizableWithAutocomplete";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { LicencedDoctorAutocomplete } from "@/Common/Components/Search/LicencedDoctorAutocomplete";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { Locale } from "@/Common/Models/Localization/Locale";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { HeightUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { FaxDirectorySearchComponent } from "@/ContactDirectory/Components/Search/FaxDirectorySearchComponent";
import { FaxDirectorySearchResultItem } from "@/ContactDirectory/Components/Search/FaxDirectorySearchResultItem";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";
import { ObstetricalExamResultState } from "@/Patient/Models/ObstetricalFile/ObstetricalExamResultState";
import { AutoCompleteTextboxRenderOptions, CheckboxOptions, DateSelectorRenderOptions, InlineRadioOptions, TextAreaRenderOptions, TextAreaResizableRenderOptions, TextAreaResizableWithAutocompleteRenderOptions, TextboxControlWithDatePickerRenderOption, TextboxRenderOptions } from "./ObstetricalFileControlRendererOptions";

interface IObstetricalFileControlRendererParams {
    readonly?: boolean;
    onBlur?: () => void;
    locale?: Locale;
}

class ObstetricalFileControlRenderer {
    private isReadonly: boolean;
    private onBlur: () => void;
    private locale: Locale;
    public readonly NEGATIVE_RHESUS = "-";
    private readonly POSITIVE_RHESUS = "+";
    private readonly TYPE_A = "A";
    private readonly TYPE_B = "B";
    private readonly TYPE_AB = "AB";
    private readonly TYPE_O = "O";

    constructor(params?: IObstetricalFileControlRendererParams) {
        this.isReadonly = params?.readonly;
        this.onBlur = params?.onBlur;
        this.locale = params?.locale;
    }

    public renderTextbox(options: TextboxRenderOptions, forwardRef?: (ref: TextboxControl) => void, containerClassName?: string): JSX.Element {
        if (!options)
            return <span />;

        if (this.isReadonly || options?.isReadOnlyOverride)
            return <span className={"field-text " + options?.className}>{options?.value}</span>;

        return (
            <div className={containerClassName ?? ""}>
                <TextboxControl
                    ref={forwardRef}
                    containerCssClass={options?.containerClassName ?? ""}
                    className={"form-control " + options?.className}
                    value={options?.value ?? ""}
                    onChange={value => {
                        if ((options.isInteger || options.isNumeric) && options.maxLength > 0 && value)
                            value = value.substring(0, options.maxLength);

                        options?.onChange?.(value);
                    }}
                    onBlur={e => { options?.onBlur?.(e); this.onBlur?.(); }}
                    onFocus={() => options?.onFocus?.()}
                    IsNumeric={options?.isNumeric}
                    isInteger={options?.isInteger}
                    min={options.min}
                    max={options.max}
                    maxLength={options?.maxLength}
                    placeholder={options?.placeholder}
                    debounceMs={options?.debounceMs}
                    id={options?.id}
                    disabled={options?.disabled}
                    tabIndex={options?.tabIndex}
                    onKeyDown={options?.onKeyDown}
                    type={options.type}
                    style={options.style}
                />
                {!this.isReadonly && options?.clickableTextOptions?.show && (
                    <ClickableTextPanel
                        leftAlign={options.clickableTextOptions.leftAlign}
                        cssClass="text-helper-container"
                        context={options.clickableTextOptions.context}
                        inputElementId={options.id}
                        maxLength={options?.maxLength}
                        onCloseEvent={() => options.clickableTextOptions.onClick?.(false)}
                    />
                )}
                {!this.isReadonly && options?.clickableTextOptions && (
                    <div className={`text-right ${options?.clickableTextOptions.selectFromListCssClass}`}>
                        <a
                            onClick={e => {
                                e.stopPropagation();
                                options.clickableTextOptions.onClick?.(true);
                            }}
                        >
                            {cacheStore.GetString("SelectFromListLabel", null, this.locale)}
                        </a>
                    </div>
                )}
            </div>
        );
    }

    public renderAutoCompleteTextbox<TModel>(options: AutoCompleteTextboxRenderOptions<TModel>): JSX.Element {
        if (!options)
            return <span />;

        if (this.isReadonly || options?.isReadOnlyOverride)
            return <span className={"field-text " + options?.className}>{options?.value}</span>;

        return (
            <TextboxControl
                id={options?.id}
                className={options?.className}
                value={options?.value ?? ""}
                autoCompleteItems={Immutable.List(options.autoCompleteItems)}
                onChange={value => options?.onChange?.(value)}
                onAutoCompleteSelectCallBack={(code, model: TModel, value) => options?.onAutoCompleteSelected?.(code, model, value)}
                onBlur={e => { options?.onBlur?.(e); this.onBlur?.(); }}
                onFocus={() => options?.onFocus?.()}
                maxLength={options?.maxLength}
                IsNumeric={options?.isNumeric}
                min={options.min}
                max={options.max}
                listWidth={options?.listWidth ?? ""}
                disableMatchAutoSorting={options.disableMatchAutoSorting}
                debounceMs={options?.debounceMs}
                disabled={options?.disabled}
                tabIndex={options?.tabIndex}
                type={options?.type}
            />
        );
    }

    public renderTextboxWithNormalOption(
        value: string,
        onChange?: (value: string) => void,
        maxLength?: number,
        className?: string
    ): JSX.Element {
        return this.renderAutoCompleteTextbox({
            value: value,
            autoCompleteItems: [new SearchableItemModelT<string>(cacheStore.GetString("NormalLabel", null, this.locale), "normal")],
            onChange: value => onChange?.(value),
            onAutoCompleteSelected: (code, model, value) => onChange?.(value),
            maxLength: maxLength,
            className: className,
            disableMatchAutoSorting: true
        });
    }

    public renderTextboxWithPositiveNegativeOption(options: TextboxRenderOptions): JSX.Element {
        return this.renderAutoCompleteTextbox({
            value: options?.value,
            autoCompleteItems: [
                new SearchableItemModelT<string>(cacheStore.GetString("Positive", null, this.locale), "Positive"),
                new SearchableItemModelT<string>(cacheStore.GetString("Negative", null, this.locale), "Negative")
            ],
            onChange: value => options?.onChange?.(value),
            onAutoCompleteSelected: (code, model, value) => options?.onChange?.(value),
            maxLength: options?.maxLength,
            className: options?.className,
            disableMatchAutoSorting: true
        });
    }

    public renderWeekTextbox(
        value: string,
        onChange?: (value: string) => void,
        maxLength?: number,
        className?: string,
        id?: string
    ): JSX.Element {
        if (this.isReadonly)
            return <p className={"field-text " + className}>{value}</p>;

        const locale = this.locale ?? sessionStore.locale;

        return (
            <TextboxMaskedControl
                id={id}
                className={className}
                value={value ?? ""}
                mask={locale == Locale.fr ? "99\\s 9j" : "99w 9\\d"}
                isValid
                allowEmpty
                returnMaskedValue
                onChangeFunction={value => onChange?.(value)}
                onBlurFunction={() => this.onBlur?.()}
                maxLength={maxLength}
            />
        );
    }

    public renderTextarea(options: TextAreaRenderOptions): JSX.Element {
        if (this.isReadonly)
            return <p className="field-text text-wrap-space">{options.value}</p>;

        return (
            <div>
                <textarea
                    id={options?.id}
                    className={`form-control ${options?.className ?? ""}`}
                    value={options?.value ?? ""}
                    onKeyDown={e => {
                        if (this.isNewlineExceedingMaxRow(e.keyCode, options?.maxRows, options?.value))
                            e.preventDefault();
                    }}
                    onChange={(e: any) => options?.onChange?.(e.target.value)}
                    onBlur={() => this.onBlur?.()}
                    maxLength={options?.maxLength}
                />
                {!this.isReadonly && options?.clickableTextOptions?.show && (
                    <ClickableTextPanel
                        leftAlign={options.clickableTextOptions.leftAlign}
                        cssClass="text-helper-container"
                        context={options.clickableTextOptions.context}
                        inputElementId={options.id}
                        maxLength={options?.maxLength}
                        onCloseEvent={() => options.clickableTextOptions.onClick?.(false)}
                    />
                )}
                {!this.isReadonly && options?.clickableTextOptions && (
                    <div className={`text-right ${options?.clickableTextOptions.selectFromListCssClass}`}>
                        <a
                            onClick={e => {
                                e.stopPropagation();
                                options.clickableTextOptions.onClick?.(true);
                            }}
                        >
                            {cacheStore.GetString("SelectFromListLabel", null, this.locale)}
                        </a>
                    </div>
                )}
            </div>
        );
    }

    public renderResizableTextarea(options: TextAreaResizableRenderOptions): JSX.Element {
        if (this.isReadonly)
            return <p className="field-text text-wrap-space">{options?.value}</p>;

        return (
            <div className={options?.containerCssClass}>
                <SimpleTextareaResizable
                    id={options?.id}
                    value={options?.value ?? ""}
                    onKeyDown={e => {
                        if (this.isNewlineExceedingMaxRow(e.keyCode, options?.maxRows, options?.value))
                            e.preventDefault();
                    }}
                    onChange={(e: any) => options?.onChange?.(e.target.value)}
                    onBlur={() => this.onBlur?.()}
                    maxLength={options?.maxLength}
                    minHeight={options?.minHeight}
                    maxHeight={options?.maxHeight}
                    disabled={options?.isDisabled}
                    className={options?.className}
                />
                {!this.isReadonly && options?.clickableTextOptions?.show && (
                    <ClickableTextPanel
                        leftAlign={options.clickableTextOptions.leftAlign}
                        cssClass="text-helper-container"
                        context={options.clickableTextOptions.context}
                        inputElementId={options.id}
                        maxLength={options?.maxLength}
                        onCloseEvent={() => options.clickableTextOptions.onClick?.(false)}
                    />
                )}
                {!this.isReadonly && options?.clickableTextOptions && (
                    <div className={`text-right ${options?.clickableTextOptions.selectFromListCssClass}`}>
                        <a
                            onClick={e => {
                                e.stopPropagation();
                                options.clickableTextOptions.onClick?.(true);
                            }}
                        >
                            {cacheStore.GetString("SelectFromListLabel", null, this.locale)}
                        </a>
                    </div>
                )}
            </div>
        );
    }

    public renderResizableTextareaWithAutocomplete(options: TextAreaResizableWithAutocompleteRenderOptions): JSX.Element {
        if (this.isReadonly)
            return <p className="field-text text-wrap-space">{options?.value}</p>;

        return (
            <SimpleTextareaResizableWithAutocomplete
                id={options?.id}
                value={options?.value ?? ""}
                onKeyDown={e => {
                    if (this.isNewlineExceedingMaxRow(e.keyCode, options?.maxRows, options?.value))
                        e.preventDefault();
                }}
                onChange={(e: any) => options?.onChange?.(e.target.value)}
                onBlur={() => this.onBlur?.()}
                maxLength={options?.maxLength}
                minHeight={options?.minHeight}
                maxHeight={options?.maxHeight}
                disabled={options?.isDisabled}
                className={options?.className}
                autoCompleteItems={options?.autoCompleteItems}
                onAutoCompleteItemSelected={(selectedText: string) => options?.onChange?.(selectedText)}
            />
        );
    }

    public renderSelect(
        value: string,
        options: { display: string, value: string }[],
        onChange?: (value: string) => void,
        allowEmpty?: boolean,
        className?: string
    ): JSX.Element {
        if (this.isReadonly)
            return <span className={"field-text " + className}>{options?.firstOrDefault(p => p.value == value)?.display}</span>;

        return (
            <select className={"form-control " + className} value={value} onChange={(e: any) => onChange?.(e.target?.value)} onBlur={() => this.onBlur?.()}>
                {allowEmpty && <option value={null} />}
                {options?.map(p => <option key={p.value} value={p.value}>{p.display}</option>)}
            </select>
        );
    }

    public renderWeightUnitSelect(
        value: WeightUnit,
        onChange?: (value: WeightUnit) => void,
        className?: string,
        locale?: Locale
    ): JSX.Element {
        const options = {
            [WeightUnit.Kilogram.toString()]: cacheStore.GetString("KilogramUnitSymbolLabel", {}, locale),
            [WeightUnit.Pound.toString()]: cacheStore.GetString("PoundUnitSymbolLabel", {}, locale)
        };

        return this.renderSelect(
            value?.toString(),
            Object.keys(options).map(p => ({ display: options[p], value: p })),
            value => value && onChange?.(Number(value) as WeightUnit),
            false,
            className
        );
    }

    public renderHeightUnitInchMeterSelect(
        value: HeightUnit,
        onChange?: (value: HeightUnit) => void,
        className?: string
    ): JSX.Element {
        const options = {
            [HeightUnit.Meter.toString()]: cacheStore.GetString("MeterUnitSymbolLabel"),
            [HeightUnit.Inch.toString()]: cacheStore.GetString("InchUnitSymbolLabel")
        };

        return this.renderSelect(
            value?.toString(),
            Object.keys(options).map(p => ({ display: options[p], value: p })),
            value => value && onChange?.(Number(value) as HeightUnit),
            false,
            className
        );
    }

    public renderHeightUnitSelectInchCentimeter(
        value: HeightUnit,
        onChange?: (value: HeightUnit) => void,
        className?: string,
        locale?: Locale
    ): JSX.Element {
        const options = {
            [HeightUnit.Centimeter.toString()]: cacheStore.GetString("CentimeterUnitSymbolLabel", {}, locale),
            [HeightUnit.Inch.toString()]: cacheStore.GetString("InchUnitSymbolLabel", {}, locale)
        };

        return this.renderSelect(
            value?.toString(),
            Object.keys(options).map(p => ({ display: options[p], value: p })),
            value => value && onChange?.(Number(value) as HeightUnit),
            false,
            className
        );
    }

    public renderDateSelector(options: DateSelectorRenderOptions, readonlyClassName: string = ""): JSX.Element {
        options = new DateSelectorRenderOptions(options);

        if (this.isReadonly)
            return <span id={options.id} className={`${readonlyClassName} field-text`}>{DateHelper.formatDate(options.value, options.dateFormatWhenReadonly)}</span>;

        return (
            <DateSelector
                id={options.id}
                date={options.value}
                onChangeCallback={date => options.onChange?.(date)}
                onBlur={() => this.onBlur?.()}
                dateFormat={options.dateFormat}
                valid={options.isValid}
                cssClass={`date-selector ${options?.className ?? ""}`}
                containerClassName={options.containerClassName ?? ""}
                isMonthPicker={options.isMonthPicker}
                height={options.height ?? 34}
                width={options.width}
                maxDate={options.maxDate}
                ignoreInvalidCharacters={options.ignoreInvalidCharacters}
                ref={options.ref}
            />
        );
    }

    public renderDateWithPrecision(options: TextboxControlWithDatePickerRenderOption): JSX.Element {
        options = new TextboxControlWithDatePickerRenderOption(options);

        const dateWithPrecision = new DateTimeWithPrecision(null, null);
        dateWithPrecision.value = options.date;
        dateWithPrecision.text = options.date?.toString();
        dateWithPrecision.precision = options.precision;

        const formattedDate = dateWithPrecision.getFormattedDate();

        if (this.isReadonly)
            return <span className="field-text">{formattedDate}</span>;

        return (
            <TextboxControlWithDatePicker
                id={options.id}
                dateValue={options.date}
                textValue={formattedDate}
                onChange={(date: DateTimeWithPrecision) => (IsNullOrWhiteSpace(date.text) || date.IsValidYear(+date.text?.substr(0, 4))) && options.onChange?.(date)}
                cssClass={options.className}
                width={options.width}
                isMonthPicker={options.isMonthPicker}
            />
        );
    }

    public renderYesNoRadio(value: boolean, onChange: (value: boolean) => void, id: string, containerStyle: React.CSSProperties = {}): JSX.Element {
        let options = [
            { Key: cacheStore.GetString("YesLabel", null, this.locale), Value: true, Active: true },
            { Key: cacheStore.GetString("NoLabel", null, this.locale), Value: false, Active: true }
        ];

        return (
            <div className="radio-btn" style={containerStyle}>
                <RadioButton
                    mainDivCSS=""
                    radioInputGroupId={id}
                    selectedItem={value}
                    items={Immutable.List(options)}
                    onChange={item => onChange?.(item.Value)}
                    disabled={this.isReadonly}
                    onBlur={() => this.onBlur?.()}
                />
            </div>
        );
    }

    public renderPositiveNegativeCheckBoxes(value: boolean, onChange: (value: boolean) => void, id: string): JSX.Element {
        const options = [
            new InlineOption<boolean>(true, "+"),
            new InlineOption<boolean>(false, "-")
        ];
        return this.renderUniqueCheckBoxList(options, value, onChange, id);
    }

    public renderUniqueCheckBoxList<T>(items: InlineOption<T>[], value: T, onChange: (value: T) => void, id: string): JSX.Element {
        return (
            <InlineOptionsListControl
                id={id}
                items={items}
                value={value}
                onChange={option => onChange?.(option?.Key)}
                disabled={this.isReadonly}
                onBlur={() => this.onBlur?.()}
                allowDeselect
            />
        );
    }

    public renderInlineYesNoRadio(
        value: boolean | null,
        onChange: (value: boolean) => void,
        id: string,
        className: string = "",
        tabIndex?: number,
        reverseOrder?: boolean
    ): JSX.Element {
        return (
            <div className={`yes-or-no-radios-container ${className}`}>
                <InlineOptionsListControl.YesOrNo
                    id={id}
                    value={value}
                    onChange={option => onChange?.(option?.Key)}
                    disabled={this.isReadonly}
                    locale={this.locale}
                    abbreviate
                    optionSpacing="10px"
                    tabIndex={tabIndex}
                    reverseOrder={reverseOrder}
                />
            </div>
        );
    }

    public renderRadioButtonWithLabel(
        id: string,
        name: string,
        labelText: string,
        checked: boolean,
        disabled: boolean,
        onChange: (event: React.FormEvent) => void,
        renderLabelFirst: boolean,
        className?: string,
        labelClassName?: string,
        isCheckbox?: boolean
    ): JSX.Element[] {
        let result = [
            <label htmlFor={id} key={`${id}Label`} className={labelClassName}>{labelText}</label>,
            <input
                type={isCheckbox ? "checkbox" : "radio"}
                id={id}
                name={name}
                className={className}
                disabled={disabled}
                checked={checked ?? false}
                onChange={event => onChange(event)}
                onBlur={() => this.onBlur?.()}
                key={`${id}radioButton`}
            />
        ];

        if (!renderLabelFirst)
            result.reverse();

        return result;
    }

    public renderCheckbox(
        label: string,
        value: boolean,
        onChange?: (value: boolean) => void,
        className?: string,
        labelClassName?: string
    ): JSX.Element {
        return (
            <div className={className}>
                <CheckboxControl
                    checked={value}
                    label={cacheStore.GetString(label, null, this.locale ?? sessionStore.locale)}
                    onChange={value => onChange?.(value)}
                    disabled={this.isReadonly}
                    labelClassName={labelClassName}
                />
            </div>
        );
    }

    public renderCheckboxWithOptions(options: CheckboxOptions): JSX.Element {
        return (
            <div className={options.containerClassName} style={options.containerStyle}>
                <CheckboxControl
                    {...options}
                    label={cacheStore.GetString(options.label, null, this.locale ?? sessionStore.locale)}
                    onChange={value => options.onChange?.(value)}
                    disabled={this.isReadonly}
                    labelClassName={options.labelClassName}
                />
            </div>
        );
    }

    public renderInlineRadio<T>(options: InlineRadioOptions<T>): JSX.Element {
        return (
            <InlineOptionsListControl
                id={options.id}
                value={options.value}
                items={options.items}
                onChange={option => options.onChange(option?.Key)}
                disabled={this.isReadonly}
                className={options.className}
                additionalElements={options.additional}
                allowDeselect={options.allowDeselect}
                additionalWrapperClassName={options.additionalWrapperClassName}
                optionSpacing={options.optionSpacing}
                wrapOptions={options.wrapOptions}
                heightInPixels={options.heightInPixels}
            />
        );
    }

    public renderPhysicianSearchTextbox(
        value: string,
        onChange?: (value: string, isItemSelected: boolean) => void,
        className?: string,
        id?: string,
        maxLength?: number
    ): JSX.Element {
        if (this.isReadonly)
            return <span className={"field-text " + className}>{value}</span>;

        return (
            <FeatureFlagControl
                flag={cacheStore.ClinicSettings.EnableContactDirectory}
                activeFeature={(
                    <FaxDirectorySearchComponent
                        componentId={id}
                        entityTypes={[DirectoryEntityType.HealthcareProvider]}
                        showClinicHealthCareProviders={true}
                        isValid={true}
                        disableEdit={true}
                        onItemSelected={(selection: FaxDirectorySearchResultItem) => {
                            const provider = selection.entity as DirectoryHealthCareProvider;
                            let text = provider ? `${provider.lastName} ${provider.firstName}` : "";
                            text = maxLength >= 0 ? text.substr(0, maxLength) : text;
                            onChange?.(text, true);
                        }}
                        maxLength={maxLength}
                        searchText={value}
                        onSearchTextChanged={text => onChange?.(text, true)}
                        clearSearchTextOnSelect={false}
                        dataCy="contactDirectory-obstetricalFile-healthCareProviderSearch"
                    />
                )}
                fallbackFeature={(
                    <LicencedDoctorAutocomplete
                        componentId={id}
                        onItemSelectCallback={item => {
                            let text = item ? item.Name : "";

                            text = maxLength >= 0 ? text.substr(0, maxLength) : text;
                            onChange?.(text, true);
                        }}
                        defaultText={value}
                        text={value}
                        getClinicianDoctors={() => cacheStore.GetClinicianDoctors()}
                        textChangedFunction={value => onChange?.(maxLength >= 0 ? value?.substr(0, maxLength) : value, false)}
                        onBlur={() => this.onBlur?.()}
                        isValid={true}
                        maxLength={maxLength}
                        filterSearchResult={intervenors => {
                            const allEmployeeLicences = cacheStore.EmployeeCache
                                .map(p => p.Licences.map(c => c.LicenceNumber))
                                .flatten(p => p)
                                .filter(licence => !IsNullOrWhiteSpace(licence));

                            return [
                                ...intervenors.filter(p => !IsNullOrWhiteSpace(p.LicenceNumber) && allEmployeeLicences.any(licence => licence === p.LicenceNumber)),
                                ...intervenors.filter(p => IsNullOrWhiteSpace(p.LicenceNumber) || !allEmployeeLicences.any(licence => licence === p.LicenceNumber))
                            ];
                        }}
                    />
                )}
            />
        );
    }

    public renderNormalAbnormalRadio(
        value: ObstetricalExamResultState,
        onChange: (value: ObstetricalExamResultState) => void,
        id: string
    ): JSX.Element {
        const options = [
            new KeyValuePair(
                cacheStore.GetString("NormalAbbreviationLabel", null, this.locale),
                ObstetricalExamResultState.Normal
            ),
            new KeyValuePair(
                cacheStore.GetString("AbnormalAbbreviationLabel", null, this.locale),
                ObstetricalExamResultState.Abnormal
            )
        ];

        return (
            <div className="radio-btn">
                <RadioButton
                    mainDivCSS=""
                    radioInputGroupId={id}
                    selectedItem={value}
                    items={Immutable.List(options)}
                    onChange={item => onChange?.(item.Value)}
                    disabled={this.isReadonly}
                    onBlur={() => this.onBlur?.()}
                />
            </div>
        );
    }

    public renderComboBox(
        options: {
            id: string;
            onChange: (code: string) => void;
            selectedCode: string;
            hasEmptyItem?: boolean;
            items: ComboBoxDropDownItem[];
            className?: string;
        }
    ): JSX.Element {
        if (this.isReadonly)
            return <span className="field-text">{options?.items?.firstOrDefault(p => p.Code === options.selectedCode)?.DisplayName ?? ""}</span>;

        return (
            <ComboBoxControl
                id={options.id}
                className={`obstetrical-combobox ${options.className ?? ""}`}
                onChange={code => options.onChange(code)}
                selectedCode={options.selectedCode}
                disabled={this.isReadonly}
                hasEmptyItem={options?.hasEmptyItem}
                items={options?.items?.toImmutable()}
            />
        );
    }

    private isNewlineExceedingMaxRow(keyCode: number, maxRow: number, value: string): boolean {
        return keyCode === 13
            && maxRow
            && maxRow <= (value?.split("\n").length ?? 0);
    }

    public getBloodGroupOptions(): ComboBoxDropDownItem[] {
        return [
            new ComboBoxDropDownItem(this.TYPE_A, this.TYPE_A),
            new ComboBoxDropDownItem(this.TYPE_B, this.TYPE_B),
            new ComboBoxDropDownItem(this.TYPE_AB, this.TYPE_AB),
            new ComboBoxDropDownItem(this.TYPE_O, this.TYPE_O)
        ];
    }

    public getBloodRhesusOptions(): ComboBoxDropDownItem[] {
        return [
            new ComboBoxDropDownItem(this.POSITIVE_RHESUS, this.POSITIVE_RHESUS),
            new ComboBoxDropDownItem(this.NEGATIVE_RHESUS, this.NEGATIVE_RHESUS)
        ];
    }
}

export { ObstetricalFileControlRenderer };
